/**
 * LiveOddsTableWidget — Current normalized odds from the ABET receiver fleet
 * Groups odds by game (canonical_id), shows all bookmakers side-by-side
 * Features: Best odds w/ book logo, Average odds, Chart button, per-book columns
 * Sport & market nav pills (matching old ABET dashboard), league grouping
 * Prefix: lot-
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { liveOddsSupabase as supabase, isHtmlError, SUPABASE_DOWN_MESSAGE } from '../lib/supabase'
import { buildReceiverStreamUrl, fetchReceiverBoard, fetchReceiverOdds } from '../lib/api'
import { fetchMarriedBoardRows } from '../lib/marriedBoard'
import {
  adaptReceiverBoardResult,
  allSettledWithConcurrency,
  summarizeReceiverBoardResults,
} from '../lib/receiverBoard'
import { getTeamLogo, getTeamDisplayName } from '../lib/logoMaps'
import {
  BOOK_DISPLAY_ORDER,
  canonicalBookSlug,
  resolveBookIdentity,
} from '../lib/bookLogoRegistry'
import { getLeagueLogoPath } from '../lib/leagueLogos'
import {
  buildNormalizedOutcomeKey,
  extractEventDateKey,
  getNormalizedTeamDisplay,
  isEffectivelyLive,
  isBoardVisibleRow,
  isLikelyInsertionTimestamp,
  isPregameStatus,
  normalizeLeagueCode,
  normalizeSelectionLabel,
  normalizeTeamCode,
  normalizeOddsRow,
  normalizeText,
} from '../lib/oddsNormalization'
import { RefreshCw, Star, TrendingUp, ChevronDown, ChevronUp, BarChart3, Search } from 'lucide-react'
import OddsChartModal from './OddsChartModal'
import LiveOddsGameCard from './LiveOddsGameCard'
import OddsGridView from './OddsGridView'
import OddsMatrixView from './OddsMatrixView'
import './LiveOddsTableWidget.css'

const LEAGUES = [
  { id: 'ALL', label: 'All Sports', raw: [] },
  { id: 'NFL', label: 'NFL', raw: ['NFL'] },
  { id: 'NBA', label: 'NBA', raw: ['NBA'] },
  { id: 'MLB', label: 'MLB', raw: ['MLB'] },
  { id: 'NHL', label: 'NHL', raw: ['NHL'] },
  { id: 'CFB', label: 'CFB', raw: ['CFB', 'NCAAF'] },
  { id: 'CBB', label: 'MCBB', raw: ['CBB', 'NCAAB', 'NCAAM'] },
  { id: 'NCAAW', label: 'WCBB', raw: ['NCAAW'] },
  { id: 'SOCCER', label: 'Soccer', raw: ['SOCCER'] },
  { id: 'MMA', label: 'MMA', raw: ['MMA', 'UFC'] },
  { id: 'TENNIS', label: 'Tennis', raw: ['TENNIS'] },
]

const MARKETS = [
  { id: 'ALL', label: 'All Markets' },
  { id: 'ML', label: 'Moneyline' },
  { id: 'SPREAD', label: 'Spread' },
  { id: 'TOTAL', label: 'Total' },
]

const EXPANSION_LAB_BOOKS = ['Winamax', 'PMU', 'Betsafe', 'Betsson', 'BetAnything', 'Betr AU', 'PlayUp', 'Coral', 'Ladbrokes', 'Grosvenor', 'betPARX', 'ATG', 'Svenska Spel', 'LiveScore Bet', 'Virgin Bet', 'TABtouch', 'NetBet', 'Unibet AU', 'Unibet FR', 'Betway', 'BetOpenly', 'Everygame', 'Matchbook', 'Marathonbet', 'Novig', 'ProphetX', 'ReBet', 'Smarkets', 'Sporttrade']
const EXPANSION_LEAGUE_LOAD_ORDER = ['NBA', 'MLB', 'NHL', 'NFL', 'NCAAB', 'NCAAW', 'CFB', 'SOCCER', 'MMA', 'TENNIS']
const LIVE_ODDS_PAGE_SIZE = 1000
const LIVE_ODDS_MAX_ROWS = 25000
const RECEIVER_RETRY_LIMITS = [2000, 1000, 500]
const RECEIVER_FETCH_PLAN = {
  ALL: [
    { sport: 'MLB', limit: 2000 },
    { sport: 'NBA', limit: 2000 },
    { sport: 'NHL', limit: 2000 },
    { sport: 'NCAAB', limit: 1000 },
    { sport: 'SOCCER', limit: 750 },
    { sport: 'NFL', limit: 500 },
    { sport: 'CFB', limit: 500 },
    { sport: 'NCAAW', limit: 500 },
    { sport: 'MMA', limit: 500 },
    { sport: 'TENNIS', limit: 500 },
  ],
  NFL: [{ sport: 'NFL', limit: 500 }],
  NBA: [{ sport: 'NBA', limit: 5000 }],
  MLB: [{ sport: 'MLB', limit: 5000 }],
  NHL: [{ sport: 'NHL', limit: 5000 }],
  CFB: [{ sport: 'CFB', limit: 500 }],
  CBB: [{ sport: 'NCAAB', limit: 1000 }],
  NCAAW: [{ sport: 'NCAAW', limit: 500 }],
  SOCCER: [{ sport: 'SOCCER', limit: 750 }],
  MMA: [{ sport: 'MMA', limit: 500 }],
  TENNIS: [{ sport: 'TENNIS', limit: 500 }],
}
const RECEIVER_BOARD_PLAN = {
  NFL: { sport: 'NFL', limit: 1000 },
  NBA: { sport: 'NBA', limit: 5000 },
  MLB: { sport: 'MLB', limit: 5000 },
  NHL: { sport: 'NHL', limit: 5000 },
  CFB: { sport: 'CFB', limit: 2000 },
  CBB: { sport: 'NCAAB', limit: 3000 },
  NCAAW: { sport: 'NCAAW', limit: 1500 },
  SOCCER: { sport: 'SOCCER', limit: 2000 },
  MMA: { sport: 'MMA', limit: 1000 },
  TENNIS: { sport: 'TENNIS', limit: 1000 },
}
const LIVE_ODDS_PAST_WINDOW_HOURS = 6
const LIVE_ODDS_FUTURE_WINDOW_HOURS = 24 * 370
const LIVE_ODDS_CLOCK_REFRESH_MS = 10000
const LIVE_ODDS_SYNC_REFRESH_MS = 45000
const RECEIVER_STREAM_RECONNECT_MS = 3000
const RECEIVER_ODDS_FALLBACK_TIMEOUT_MS = 45000
const RECEIVER_ALL_BOARD_CONCURRENCY = 3
const START_TIME_BUCKET_MS = 30 * 60_000
const TEAM_ONLY_MERGE_THRESHOLD_MS = 4 * 3600_000
const BOARD_SELECT_FIELDS = 'compound_key,canonical_id,participants,away_team,home_team,start_time,league,bookmaker,market_normalized,selection,american_odds,handicap,is_live,game_status,home_score,away_score,period,clock,updated_at'
const EXCLUDED_BOOKMAKERS = new Set(['testbook'])
const LIVE_ODDS_CACHE_PREFIX = 'abet_live_odds_cache_v5_'
const LIVE_ODDS_CACHE_TTL_MS = 60_000
const EXPANSION_CACHE_PREFIX = 'abet_expansion_odds_v3_'
const EXPANSION_CACHE_TTL_MS = 10 * 60_000
const ENABLE_STATIC_EXPANSION_FEED = import.meta.env.VITE_ENABLE_STATIC_EXPANSION_FEED === 'true'
const DEFAULT_BOOT_LEAGUE = 'ALL'
const LIVE_ODDS_LEAGUE_PREFERENCE_KEY = 'abet_live_odds_selected_league_v1'
const LIVE_ODDS_HIDDEN_BOOKS_PREFERENCE_KEY = 'abet_live_odds_hidden_books_v1'
const LIVE_ODDS_SHOW_INACTIVE_BOOKS_PREFERENCE_KEY = 'abet_live_odds_show_inactive_books_v3'
const BOARD_RUNTIME_MARKETS = new Set(['ML', 'SPREAD', 'TOTAL'])

const leagueLookup = new Map(LEAGUES.map((entry) => [entry.id, entry]))
const VALID_LEAGUE_IDS = new Set(LEAGUES.map((entry) => entry.id))

function formatOdds(american, format) {
  if (american == null) return '—'
  if (format === 'decimal') {
    const dec = american > 0
      ? (american / 100) + 1
      : (100 / Math.abs(american)) + 1
    return dec.toFixed(2)
  }
  return american > 0 ? `+${american}` : `${american}`
}

function averageOdds(oddsArr) {
  const valid = oddsArr.filter(v => v != null)
  if (valid.length === 0) return null
  return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
}

function timeUntil(dateStr) {
  if (!dateStr) return ''
  const diff = new Date(dateStr) - Date.now()
  if (diff < 0) return ''
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`
  return `${Math.floor(diff / 86400000)}d`
}

function formatLine(handicap, market, selection) {
  if (handicap == null) return ''
  if (market === 'TOTAL') {
    const prefix = (selection || '').toLowerCase().includes('over') ? 'O' : 'U'
    return `${prefix} ${Math.abs(handicap)}`
  }
  if (handicap === 0) return 'PK'
  return handicap > 0 ? `+${handicap}` : `${handicap}`
}

function formatStartTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

function getLeagueBucket(rawLeague) {
  const league = String(rawLeague || '').toUpperCase()
  // Keep Women's CBB separate from Men's CBB for clear grouping
  if (league === 'NCAAW') return 'NCAAW'
  if (league === 'NCAAB' || league === 'NCAAM' || league === 'CBB') return 'CBB'
  if (league === 'NCAAF' || league === 'CFB') return 'CFB'
  return league || 'OTHER'
}

const LEAGUE_DISPLAY_LABELS = {
  NFL: 'NFL',
  NBA: 'NBA',
  MLB: 'MLB',
  NHL: 'NHL',
  CBB: "Men's College Basketball",
  NCAAW: "Women's College Basketball",
  CFB: 'College Football',
  SOCCER: 'Soccer',
  MMA: 'MMA',
  TENNIS: 'Tennis',
  OTHER: 'Other',
}

function matchesLeagueFilter(row, selectedLeague) {
  if (selectedLeague.id === 'ALL') return true
  const normalizedLeague = normalizeLeagueCode(row.league)
  const rawSet = new Set(selectedLeague.raw.map((entry) => normalizeLeagueCode(entry)))
  return rawSet.has(normalizedLeague)
}

function matchesMarketFilter(row, marketFilter) {
  if (marketFilter === 'ALL') return true
  return String(row.market_normalized || '').toUpperCase() === marketFilter
}

function buildNormalizedPairKey(row) {
  const teams = [
    normalizeTeamCode(row.away_team, row.league),
    normalizeTeamCode(row.home_team, row.league),
  ].sort()
  const eventDateKey = extractEventDateKey(row.canonical_id, row.start_time)

  return [
    normalizeLeagueCode(row.league),
    eventDateKey,
    ...teams,
  ].join('|')
}

function buildOrientationKey(awayTeam, homeTeam, league) {
  return [
    normalizeTeamCode(awayTeam, league),
    normalizeTeamCode(homeTeam, league),
  ].join('|')
}

function buildNormalizedTeamOnlyKey(row) {
  const teams = [
    normalizeTeamCode(row.away_team, row.league),
    normalizeTeamCode(row.home_team, row.league),
  ].sort()

  return [
    normalizeLeagueCode(row.league),
    ...teams,
  ].join('|')
}

function getEventMergeThresholdMs(league) {
  const normalizedLeague = normalizeLeagueCode(league)
  if (normalizedLeague === 'MLB') return 12 * 3600_000
  if (normalizedLeague === 'NCAAB' || normalizedLeague === 'NCAAW' || normalizedLeague === 'CBB') return 18 * 3600_000
  return 18 * 3600_000
}

function getExpansionLeagueRequests(selectedLeagueId) {
  if (selectedLeagueId === 'ALL') return EXPANSION_LEAGUE_LOAD_ORDER

  const selectedLeague = leagueLookup.get(selectedLeagueId) || leagueLookup.get('ALL')
  const normalized = Array.from(
    new Set(
      (selectedLeague?.raw || [])
        .map((entry) => normalizeLeagueCode(entry))
        .flatMap((entry) => {
          if (entry === 'CBB') return ['NCAAB', 'NCAAW']
          return [entry]
        })
        .filter(Boolean)
    )
  )

  return normalized.length > 0 ? normalized : EXPANSION_LEAGUE_LOAD_ORDER
}

function parseTimestampMs(value) {
  const parsed = value ? new Date(value).getTime() : Number.NaN
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

function hasScoreValue(value) {
  if (value == null || value === '') return false
  return Number.isFinite(Number(value))
}

function hasScoreboardEvidence(snapshot) {
  if (hasScoreValue(snapshot?.home_score) || hasScoreValue(snapshot?.away_score)) {
    return true
  }

  const period = String(snapshot?.period || '').trim()
  const clock = String(snapshot?.clock || '').trim()
  if (!period && !clock) return false
  if (
    isPregameStatus(snapshot?.game_status) ||
    isPregameStatus(period) ||
    isPregameStatus(clock)
  ) {
    return false
  }

  return Boolean(period || clock)
}

function compareBookmakers(left, right) {
  const leftSlug = canonicalBookSlug(left)
  const rightSlug = canonicalBookSlug(right)
  const leftIndex = BOOK_DISPLAY_ORDER.indexOf(leftSlug)
  const rightIndex = BOOK_DISPLAY_ORDER.indexOf(rightSlug)
  if (leftIndex !== -1 || rightIndex !== -1) {
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    if (leftIndex !== rightIndex) return leftIndex - rightIndex
  }
  return resolveBookIdentity(leftSlug).displayName.localeCompare(resolveBookIdentity(rightSlug).displayName)
}

function readJsonCache(key, maxAgeMs) {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const writtenAt = Number(parsed?.writtenAt || 0)
    if (!Number.isFinite(writtenAt) || Date.now() - writtenAt > maxAgeMs) return null
    return parsed?.rows || null
  } catch {
    return null
  }
}

function writeJsonCache(key, rows) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, JSON.stringify({
      writtenAt: Date.now(),
      rows,
    }))
  } catch {
    // Ignore cache write failures
  }
}

function readLocalPreference(key, fallback = null) {
  if (typeof window === 'undefined') return fallback
  try {
    const value = window.localStorage.getItem(key)
    return value == null ? fallback : JSON.parse(value)
  } catch {
    return fallback
  }
}

function writeLocalPreference(key, value) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore local preference write failures
  }
}

function getStoredLeaguePreference() {
  const value = readLocalPreference(LIVE_ODDS_LEAGUE_PREFERENCE_KEY, null)
  return typeof value === 'string' && VALID_LEAGUE_IDS.has(value) ? value : null
}

function getStoredHiddenBooks() {
  const value = readLocalPreference(LIVE_ODDS_HIDDEN_BOOKS_PREFERENCE_KEY, [])
  if (!Array.isArray(value)) return []
  return Array.from(new Set(
    value
      .filter((entry) => typeof entry === 'string' && entry.trim())
      .map(canonicalBookSlug)
      .filter(Boolean)
  ))
}

function getStoredShowInactiveBooks() {
  return readLocalPreference(LIVE_ODDS_SHOW_INACTIVE_BOOKS_PREFERENCE_KEY, false) === true
}

function getPreferredVisibleLeague(leagueCounts = {}) {
  for (const entry of LEAGUES) {
    if (entry.id === 'ALL') continue
    if ((leagueCounts?.[entry.id] || 0) > 0) return entry.id
  }
  return DEFAULT_BOOT_LEAGUE
}

function getLiveOddsCacheKey(selectedLeague) {
  return `${LIVE_ODDS_CACHE_PREFIX}${selectedLeague || 'ALL'}`
}

function getReceiverFetchRequests(selectedLeague) {
  return RECEIVER_FETCH_PLAN[selectedLeague] || RECEIVER_FETCH_PLAN.ALL
}

function getReceiverBoardRequest(selectedLeague) {
  return RECEIVER_BOARD_PLAN[selectedLeague] || { sport: selectedLeague, limit: 5000 }
}

function getReceiverAttemptLimits(limit) {
  const requested = Number(limit)
  return Array.from(new Set([requested, ...RECEIVER_RETRY_LIMITS.filter((entry) => entry < requested)]))
}

async function fetchReceiverRequestWithRetry({ sport, limit }) {
  const attemptLimits = getReceiverAttemptLimits(limit)
  let lastResult = null

  for (const attemptLimit of attemptLimits) {
    const result = await fetchReceiverOdds(
      { sport, limit: String(attemptLimit) },
      { timeoutMs: RECEIVER_ODDS_FALLBACK_TIMEOUT_MS }
    )
    if (result.status === 'ok' && Array.isArray(result.data?.odds) && result.data.odds.length > 0) {
      const now = Date.now()
      const minStartMs = now - LIVE_ODDS_PAST_WINDOW_HOURS * 3600_000
      const maxStartMs = now + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600_000
      const visibleRows = mergeReceiverRows([result])
        .map(normalizeOddsRow)
        .filter((row) => isBoardVisibleRow(row, now, minStartMs, maxStartMs))

      if (visibleRows.length > 0) {
        return result
      }
    }
    lastResult = result
  }

  return lastResult
}

async function fetchReceiverBoardRequest({ sport, limit }) {
  const result = await fetchReceiverBoard({
    sport,
    limit: String(limit || 5000),
  })
  return adaptReceiverBoardResult(result)
}

function mapReceiverRow(outcome) {
  const bookmakerRaw = outcome.bookmaker
  const bookmaker = canonicalBookSlug(bookmakerRaw)
  const compoundKey = outcome.compound_key || `${outcome.canonical_id}-${bookmaker}-${outcome.market_normalized}-${outcome.selection}-${outcome.handicap || ''}`
  return {
    id: compoundKey,
    compound_key: compoundKey,
    canonical_id: outcome.canonical_id,
    participants: outcome.participants,
    away_team: outcome.away_team,
    home_team: outcome.home_team,
    start_time: outcome.start_time,
    league: outcome.league,
    bookmaker,
    bookmaker_raw: bookmakerRaw,
    market_normalized: outcome.market_normalized,
    selection: outcome.selection,
    american_odds: outcome.american_odds,
    handicap: outcome.handicap,
    is_live: outcome.is_live,
    game_status: outcome.game_status,
    home_score: outcome.home_score,
    away_score: outcome.away_score,
    period: outcome.period,
    clock: outcome.clock,
    updated_at: outcome.updated_at,
  }
}

function mergeReceiverRows(results) {
  const rowsByKey = new Map()

  results.forEach((result) => {
    const oddsRows = result?.data?.odds || []
    oddsRows.forEach((outcome) => {
      const row = mapReceiverRow(outcome)
      rowsByKey.set(row.compound_key || row.id, row)
    })
  })

  return Array.from(rowsByKey.values()).sort((left, right) => {
    const leftStart = new Date(left.start_time || 0).getTime()
    const rightStart = new Date(right.start_time || 0).getTime()
    return leftStart - rightStart
  })
}

function mergeOddsDatasets(primaryRows, secondaryRows) {
  const merged = new Map()

  const mergeRow = (row, priority) => {
    const normalizedSource = normalizeOddsRow({ ...row, source_priority: priority })
    const normalizedBookmaker = canonicalBookSlug(normalizedSource.bookmaker)
    const normalized = {
      ...normalizedSource,
      bookmaker: normalizedBookmaker,
      bookmaker_raw: normalizedSource.bookmaker_raw || normalizedSource.bookmaker,
    }
    const key = [
      normalized.bookmaker,
      buildNormalizedOutcomeKey(normalized),
    ].join('|')

    const existing = merged.get(key)
    const canonicalIds = new Set([...(existing?.canonical_ids || []), normalized.canonical_id].filter(Boolean))
    const selectionVariants = new Set([...(existing?.selection_variants || []), normalized.selection, normalized.normalized_selection].filter(Boolean))
    const participants = new Set([...(existing?.participants_variants || []), normalized.participants].filter(Boolean))

    const nextRow = {
      ...(existing && existing.__priority > priority ? existing : normalized),
      canonical_ids: Array.from(canonicalIds),
      selection_variants: Array.from(selectionVariants),
      participants_variants: Array.from(participants),
      source_priority: Math.max(existing?.source_priority ?? 0, normalized.source_priority ?? 0),
      __priority: existing ? Math.max(existing.__priority, priority) : priority,
    }

    merged.set(key, nextRow)
  }

  secondaryRows.forEach((row) => mergeRow(row, 0))
  primaryRows.forEach((row) => mergeRow(row, 1))

  return Array.from(merged.values())
    .map(({ __priority, ...row }) => row)
    .sort((left, right) => new Date(left.start_time) - new Date(right.start_time))
}

function sortRowsByStartTime(rows) {
  return [...rows].sort((left, right) => {
    const leftStart = new Date(left.start_time || 0).getTime()
    const rightStart = new Date(right.start_time || 0).getTime()
    return leftStart - rightStart
  })
}

function didBoardRowChange(existingRow, nextRow) {
  if (!existingRow) return true
  return (
    existingRow.american_odds !== nextRow.american_odds ||
    existingRow.handicap !== nextRow.handicap ||
    existingRow.updated_at !== nextRow.updated_at ||
    existingRow.start_time !== nextRow.start_time ||
    existingRow.is_live !== nextRow.is_live ||
    existingRow.game_status !== nextRow.game_status ||
    existingRow.home_score !== nextRow.home_score ||
    existingRow.away_score !== nextRow.away_score ||
    existingRow.period !== nextRow.period ||
    existingRow.clock !== nextRow.clock
  )
}

function buildScoreCanonicalId(score) {
  const league = normalizeLeagueCode(score?.sport || score?.league)
  const awayTeam = normalizeTeamCode(score?.away_team, league)
  const homeTeam = normalizeTeamCode(score?.home_team, league)
  if (!league || !awayTeam || !homeTeam) return null
  const dateKey = extractEventDateKey(score?.canonical_id, score?.start_time)
  return `${league}_${dateKey}_${awayTeam}_${homeTeam}`
}

function applyReceiverOddsUpdates(currentRows, updates, selectedLeague, nowMs, minVisibleStartMs, maxVisibleStartMs) {
  if (!Array.isArray(updates) || updates.length === 0) return null

  const nextByKey = new Map(
    currentRows.map((row) => [row.compound_key || row.id, row])
  )
  let changed = false

  updates.forEach((outcome) => {
    const normalized = normalizeOddsRow(mapReceiverRow(outcome))
    const key = normalized.compound_key || normalized.id
    if (!key) return
    if (!matchesLeagueFilter(normalized, selectedLeague)) return

    const market = String(normalized.market_normalized || '').toUpperCase()
    if (!BOARD_RUNTIME_MARKETS.has(market)) {
      if (nextByKey.delete(key)) changed = true
      return
    }

    if (!isBoardVisibleRow(normalized, nowMs, minVisibleStartMs, maxVisibleStartMs)) {
      if (nextByKey.delete(key)) changed = true
      return
    }

    const existing = nextByKey.get(key)
    if (!didBoardRowChange(existing, normalized)) return

    nextByKey.set(key, normalized)
    changed = true
  })

  return changed ? sortRowsByStartTime(Array.from(nextByKey.values())) : null
}

function applyReceiverScoreUpdates(currentRows, scoreUpdates) {
  if (!Array.isArray(scoreUpdates) || scoreUpdates.length === 0 || currentRows.length === 0) return null

  const scoreByCanonicalId = new Map()
  const scoreByNormalizedCanonicalId = new Map()

  scoreUpdates.forEach((score) => {
    if (score?.canonical_id) {
      scoreByCanonicalId.set(score.canonical_id, score)
    }
    const normalizedCanonicalId = buildScoreCanonicalId(score)
    if (normalizedCanonicalId) {
      scoreByNormalizedCanonicalId.set(normalizedCanonicalId, score)
    }
  })

  let changed = false
  const nextRows = currentRows.map((row) => {
    const score =
      scoreByCanonicalId.get(row.canonical_id) ||
      scoreByNormalizedCanonicalId.get(row.canonical_id_normalized)

    if (!score) return row

    const patched = normalizeOddsRow({
      ...row,
      home_score: score.home_score ?? row.home_score,
      away_score: score.away_score ?? row.away_score,
      period: score.period ?? row.period,
      clock: score.clock ?? row.clock,
      game_status: score.game_status || row.game_status,
      is_live: typeof score.is_live === 'boolean' ? score.is_live : row.is_live,
      updated_at: score.timestamp || row.updated_at,
    })

    if (!didBoardRowChange(row, patched)) return row
    changed = true
    return patched
  })

  return changed ? nextRows : null
}

function toStartBucketMs(startTime) {
  const startMs = new Date(startTime).getTime()
  if (!Number.isFinite(startMs)) return null
  return Math.round(startMs / START_TIME_BUCKET_MS) * START_TIME_BUCKET_MS
}

function chooseRepresentativeStartTime(startTimeVotes, nowMs, { hasLiveEvidence = false } = {}) {
  const entries = Array.from(startTimeVotes.entries())
  if (entries.length === 0) return null

  const maxFutureMs = nowMs + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600_000
  entries.sort((left, right) => {
    const [leftBucket, leftMeta] = left
    const [rightBucket, rightMeta] = right

    const leftPastPregame = !hasLiveEvidence && leftBucket < nowMs - 30 * 60_000
    const rightPastPregame = !hasLiveEvidence && rightBucket < nowMs - 30 * 60_000
    if (leftPastPregame !== rightPastPregame) return leftPastPregame ? 1 : -1

    if ((leftMeta.weight || 0) !== (rightMeta.weight || 0)) return (rightMeta.weight || 0) - (leftMeta.weight || 0)
    if (leftMeta.count !== rightMeta.count) return rightMeta.count - leftMeta.count

    const leftReasonable = leftBucket >= nowMs - 12 * 3600_000 && leftBucket <= maxFutureMs
    const rightReasonable = rightBucket >= nowMs - 12 * 3600_000 && rightBucket <= maxFutureMs
    if (leftReasonable !== rightReasonable) return leftReasonable ? -1 : 1

    return Math.abs(leftBucket - nowMs) - Math.abs(rightBucket - nowMs)
  })

  return new Date(entries[0][0]).toISOString()
}

function chooseRepresentativeTeam(teamVotes, league) {
  const entries = Array.from(teamVotes.entries())
  if (entries.length === 0) return ''

  entries.sort((left, right) => {
    const [leftTeam, leftMeta] = left
    const [rightTeam, rightMeta] = right
    const leftWeight = typeof leftMeta === 'number' ? leftMeta : leftMeta?.weight || 0
    const rightWeight = typeof rightMeta === 'number' ? rightMeta : rightMeta?.weight || 0
    const leftCount = typeof leftMeta === 'number' ? leftMeta : leftMeta?.count || 0
    const rightCount = typeof rightMeta === 'number' ? rightMeta : rightMeta?.count || 0

    if (leftWeight !== rightWeight) return rightWeight - leftWeight
    if (leftCount !== rightCount) return rightCount - leftCount

    const leftHasLogo = Boolean(getTeamLogo(leftTeam, league))
    const rightHasLogo = Boolean(getTeamLogo(rightTeam, league))
    if (leftHasLogo !== rightHasLogo) return leftHasLogo ? -1 : 1

    const leftDisplay = getTeamDisplayName(leftTeam, league) || leftTeam
    const rightDisplay = getTeamDisplayName(rightTeam, league) || rightTeam
    if (leftDisplay.length !== rightDisplay.length) return leftDisplay.length - rightDisplay.length

    return leftDisplay.localeCompare(rightDisplay)
  })

  return entries[0][0]
}

function chooseRepresentativeOrientation(orientationVotes, league, fallback) {
  const entries = Array.from(orientationVotes.entries())
  if (entries.length === 0) {
    return {
      away: normalizeTeamCode(fallback?.away_team, league),
      home: normalizeTeamCode(fallback?.home_team, league),
    }
  }

  entries.sort((left, right) => {
    const leftWeight = left[1]?.weight || 0
    const rightWeight = right[1]?.weight || 0
    const leftCount = left[1]?.count || 0
    const rightCount = right[1]?.count || 0

    if (leftWeight !== rightWeight) return rightWeight - leftWeight
    if (leftCount !== rightCount) return rightCount - leftCount
    return String(left[0]).localeCompare(String(right[0]))
  })

  const [away = '', home = ''] = String(entries[0][0]).split('|')
  return { away, home }
}

function getSnapshotWeight(snapshot, nowMs) {
  let weight = snapshot?.source_priority > 0 ? 5 : 1

  const updatedAtMs = parseTimestampMs(snapshot?.updated_at)
  if (Number.isFinite(updatedAtMs)) {
    const ageMs = Math.max(0, nowMs - updatedAtMs)
    if (ageMs <= 30 * 60_000) weight += 3
    else if (ageMs <= 2 * 3600_000) weight += 2
    else if (ageMs <= 6 * 3600_000) weight += 1
  }

  if (snapshot?.is_live_effective) weight += 3
  if (hasScoreboardEvidence(snapshot)) weight += 2

  const startMs = snapshot?.start_time ? new Date(snapshot.start_time).getTime() : Number.NaN
  if (Number.isFinite(startMs) && startMs >= nowMs - 2 * 3600_000 && startMs <= nowMs + 48 * 3600_000) {
    weight += 1
  }
  if (isCorruptedPregameStart(snapshot, nowMs)) {
    weight -= 4
  }

  return weight
}

function isCorruptedPregameStart(snapshot, nowMs) {
  if (!snapshot?.start_time) return false
  if (isLikelyInsertionTimestamp(snapshot.start_time)) return true

  const startMs = parseTimestampMs(snapshot.start_time)
  if (!Number.isFinite(startMs)) return false

  const updatedAtMs = parseTimestampMs(snapshot.updated_at)
  const pregameLike =
    !snapshot?.is_live_effective &&
    !hasScoreboardEvidence(snapshot) &&
    (!snapshot?.game_status || isPregameStatus(snapshot.game_status))

  if (!pregameLike) return false

  if (Number.isFinite(updatedAtMs) && Math.abs(startMs - updatedAtMs) <= 10 * 60_000) {
    return true
  }

  return startMs >= nowMs - 5 * 60_000 && startMs <= nowMs + 5 * 60_000
}

function getTrustedSnapshotStart(snapshot, nowMs) {
  return isCorruptedPregameStart(snapshot, nowMs) ? null : snapshot?.start_time || null
}

function getBookMonogram(book) {
  const words = String(book || '')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)

  if (words.length === 0) return 'BK'
  if (words.length === 1) {
    return words[0].replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() || 'BK'
  }
  return words.map((token) => token[0]).join('').slice(0, 3).toUpperCase()
}

function SportsbookLogo({ book, className, fallbackClassName = 'lot-book-name', showFallback = true }) {
  const identity = resolveBookIdentity(book)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [identity.logoUrl])

  if (!identity.logoUrl || failed) {
    if (!showFallback) return null
    return (
      <span className={fallbackClassName} aria-label={`${identity.displayName} logo unavailable`}>
        {getBookMonogram(identity.displayName)}
      </span>
    )
  }

  return (
    <img
      src={identity.logoUrl}
      alt={`${identity.displayName} logo`}
      title={identity.displayName}
      className={className}
      onError={() => setFailed(true)}
    />
  )
}

function chooseAuthoritativeSnapshot(bookmakerSnapshots, nowMs) {
  const snapshots = [...bookmakerSnapshots]
  snapshots.sort((left, right) => {
    const leftWeight = getSnapshotWeight(left, nowMs)
    const rightWeight = getSnapshotWeight(right, nowMs)
    if (leftWeight !== rightWeight) return rightWeight - leftWeight
    return parseTimestampMs(right?.updated_at || right?.start_time) - parseTimestampMs(left?.updated_at || left?.start_time)
  })
  return snapshots[0] || null
}

export default function LiveOddsTableWidget() {
  const [odds, setOdds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusNotice, setStatusNotice] = useState(null)
  const [dataProof, setDataProof] = useState({
    source: 'not-loaded',
    generatedAt: null,
    attemptedSports: [],
    successfulSports: [],
    rowCount: 0,
  })
  const [league, setLeague] = useState(() => getStoredLeaguePreference() || DEFAULT_BOOT_LEAGUE)
  const [marketFilter, setMarketFilter] = useState('ALL')
  const [oddsFormat, setOddsFormat] = useState('american')
  const [expandedGame, setExpandedGame] = useState(null)
  const [search, setSearch] = useState('')
  const [chartModal, setChartModal] = useState(null)
  const [showSearch, setShowSearch] = useState(false)
  const [viewMode, setViewMode] = useState('cards')
  const [hiddenBooks, setHiddenBooks] = useState(() => getStoredHiddenBooks())
  const [showInactiveTrackedBooks, setShowInactiveTrackedBooks] = useState(() => getStoredShowInactiveBooks())
  const [expansionOdds, setExpansionOdds] = useState([])
  const [loadedExpansionLeagues, setLoadedExpansionLeagues] = useState({})
  const [boardNowMs, setBoardNowMs] = useState(() => Date.now())
  const fetchRunRef = useRef(0)
  const oddsRef = useRef([])
  const fetchInFlightRef = useRef(false)
  const storedLeaguePreferenceRef = useRef(getStoredLeaguePreference())
  const receiverStreamConnectedRef = useRef(false)
  const receiverStreamLastMessageRef = useRef(0)

  useEffect(() => {
    oddsRef.current = odds
  }, [odds])

  useEffect(() => {
    writeLocalPreference(LIVE_ODDS_LEAGUE_PREFERENCE_KEY, league)
  }, [league])

  useEffect(() => {
    writeLocalPreference(LIVE_ODDS_HIDDEN_BOOKS_PREFERENCE_KEY, hiddenBooks)
  }, [hiddenBooks])

  useEffect(() => {
    writeLocalPreference(LIVE_ODDS_SHOW_INACTIVE_BOOKS_PREFERENCE_KEY, showInactiveTrackedBooks)
  }, [showInactiveTrackedBooks])

  const requestedExpansionLeagues = useMemo(
    () => (ENABLE_STATIC_EXPANSION_FEED ? getExpansionLeagueRequests(league) : []),
    [league]
  )
  const minStartMs = boardNowMs - LIVE_ODDS_PAST_WINDOW_HOURS * 3600000
  const maxStartMs = boardNowMs + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600000
  const baseOdds = useMemo(
    () => odds.filter((row) => !EXCLUDED_BOOKMAKERS.has(canonicalBookSlug(row.bookmaker)) && isBoardVisibleRow(row, boardNowMs, minStartMs, maxStartMs)),
    [odds, minStartMs, maxStartMs]
  )
  const baseExpansionOdds = useMemo(
    () => expansionOdds.filter((row) => !EXCLUDED_BOOKMAKERS.has(canonicalBookSlug(row.bookmaker)) && isBoardVisibleRow(row, boardNowMs, minStartMs, maxStartMs)),
    [expansionOdds, minStartMs, maxStartMs]
  )
  const expansionBookNames = useMemo(
    () => Array.from(new Set(baseExpansionOdds.map((row) => canonicalBookSlug(row.bookmaker))))
      .sort(compareBookmakers)
      .map((book) => resolveBookIdentity(book).displayName),
    [baseExpansionOdds]
  )
  const mergedOdds = useMemo(
    () => mergeOddsDatasets(baseOdds, baseExpansionOdds),
    [baseOdds, baseExpansionOdds]
  )
  const hiddenBookSet = useMemo(() => new Set(hiddenBooks), [hiddenBooks])
  const filteredOdds = useMemo(
    () => mergedOdds.filter((row) => !hiddenBookSet.has(row.bookmaker)),
    [mergedOdds, hiddenBookSet]
  )
  const visibleLeagueCounts = useMemo(() => {
    const counts = {}
    for (const row of filteredOdds) {
      const leagueKey = getLeagueBucket(row.league)
      counts[leagueKey] = (counts[leagueKey] || 0) + 1
    }
    return counts
  }, [filteredOdds])
  const visibleOdds = useMemo(() => {
    const selectedLeague = leagueLookup.get(league) || leagueLookup.get('ALL')
    return filteredOdds.filter((row) =>
      matchesLeagueFilter(row, selectedLeague) && matchesMarketFilter(row, marketFilter)
    )
  }, [filteredOdds, league, marketFilter])

  const fetchOdds = useCallback(async () => {
    if (fetchInFlightRef.current) {
      return
    }

    fetchInFlightRef.current = true
    try {
      setLoading(true)
      setError(null)
      setStatusNotice(null)
      const now = Date.now()
      const minVisibleStartMs = now - LIVE_ODDS_PAST_WINDOW_HOURS * 3600000
      const maxVisibleStartMs = now + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600000
      setBoardNowMs(now)
      const cacheKey = getLiveOddsCacheKey(league)
      const fetchRunId = Date.now()
      fetchRunRef.current = fetchRunId
      const selectedLeague = leagueLookup.get(league) || leagueLookup.get('ALL')

      // SOLE SOURCE (plan A3 + skill abet-no-fallbacks): the cross-book MARRIED board from the CF-KV plane —
      // the same board the proof portal (/crossbook-allbets) and the +EV/arb widgets read. There is NO
      // receiver/Supabase fallback: if the plane is unreachable we fail LOUD; we never show a thinner dataset.
      // We set ALL married rows (every league); the widget's own league/market filters do the filtering (this
      // avoids the league-scoping bug from filtering inside the fetch).
      try {
        const married = await fetchMarriedBoardRows()
        if (fetchRunRef.current !== fetchRunId) return
        if (married.rows.length > 0) {
          setOdds(married.rows)
          try { writeJsonCache(cacheKey, married.rows) } catch (cacheErr) { /* sessionStorage quota — non-fatal */ }
          setDataProof({
            source: married.source,
            generatedAt: married.generatedAt,
            attemptedSports: ['ALL'],
            successfulSports: ['ALL'],
            rowCount: married.rows.length,
            bookCount: married.bookCount,
          })
          setStatusNotice(null)
          setError(null)
          return
        }
        // plane reachable but empty -> loud, still no fallback
        setOdds([])
        setError('Live board returned no rows (data plane may be mid-publish) — it will refresh. No fallback source.')
        setLoading(false)
        return
      } catch (marriedError) {
        // plane unreachable -> FAIL LOUD, no fallback to a thinner source
        setOdds([])
        setError(`Live board (data plane) unreachable: ${marriedError?.message || 'network error'}. Retrying next refresh.`)
        setLoading(false)
        return
      }

      const receiverMessages = []
      let degradedBoardRows = null
      let liveBoardRows = null
      const preloadedReceiverResults = new Map()

      if (selectedLeague.id !== 'ALL') {
        const boardRequest = getReceiverBoardRequest(selectedLeague.id)
        const normalizedBoardSport = normalizeLeagueCode(boardRequest.sport)
        const rawFallbackPromise = fetchReceiverRequestWithRetry({
          sport: boardRequest.sport,
          limit: boardRequest.limit || 5000,
        })
        const boardResult = await fetchReceiverBoard({
          sport: boardRequest.sport,
          limit: String(boardRequest.limit || 5000),
        })
        if (fetchRunRef.current !== fetchRunId) return

        if (boardResult?.status === 'ok' && Array.isArray(boardResult.data?.rows) && boardResult.data.rows.length > 0) {
          const boardSource = String(boardResult.data?.source || '')
          const boardRows = boardResult.data.rows
            .map(normalizeOddsRow)
            .filter((row) => isBoardVisibleRow(row, now, minVisibleStartMs, maxVisibleStartMs))
            .sort((left, right) => {
              const leftStart = new Date(left.start_time || 0).getTime()
              const rightStart = new Date(right.start_time || 0).getTime()
              return leftStart - rightStart
            })

          setDataProof({
            source: boardSource || 'receiver-board',
            generatedAt: boardResult.data?.generated_at || null,
            attemptedSports: [normalizedBoardSport],
            successfulSports: [normalizedBoardSport],
            rowCount: boardResult.data.rows.length,
          })

          if (boardRows.length > 0) {
            if (boardSource === 'receiver-board-live') {
              liveBoardRows = boardRows
              oddsRef.current = boardRows
              setOdds(boardRows)
              writeJsonCache(cacheKey, boardRows)
              setStatusNotice(null)
              setError(null)
              setLoading(false)
            } else {
              degradedBoardRows = boardRows
            }
          }
        }

        if (boardResult?.message) {
          receiverMessages.push(boardResult.message)
        } else if (boardResult?.data?.message) {
          receiverMessages.push(boardResult.data.message)
        }

        if (degradedBoardRows?.length > 0) {
          receiverMessages.push('Receiver board proxy returned a degraded snapshot; falling back to live odds feed.')
        }

        const preloadedReceiverResult = await rawFallbackPromise
        preloadedReceiverResults.set(normalizedBoardSport, preloadedReceiverResult)
      }

      // PRIMARY: Fetch receiver data once per requested sport and only publish a
      // stable per-sport snapshot. All Sports uses bounded /board fan-out rather
      // than the legacy /games -> N x /odds path, so one inactive sport cannot
      // disable every other sport.
      const receiverRequests = getReceiverFetchRequests(league).map(({ sport, limit }) => ({
        sport,
        limit,
      }))
      setDataProof((previous) => ({
        ...previous,
        attemptedSports: receiverRequests.map(({ sport }) => normalizeLeagueCode(sport)),
      }))
      const receiverRowsByLeague = new Map()
      let receiverDeliveredRows = false

      const publishReceiverRows = (force = false) => {
        if (fetchRunRef.current !== fetchRunId || receiverRowsByLeague.size === 0) return

        const replacementLeagues = force && selectedLeague.id === 'ALL'
          ? new Set(receiverRequests.map(({ sport }) => normalizeLeagueCode(sport)))
          : new Set(receiverRowsByLeague.keys())

        const nextRows = [
          ...oddsRef.current.filter((row) => !replacementLeagues.has(normalizeLeagueCode(row.league))),
          ...Array.from(receiverRowsByLeague.values()).flat(),
        ].sort((left, right) => {
          const leftStart = new Date(left.start_time || 0).getTime()
          const rightStart = new Date(right.start_time || 0).getTime()
          return leftStart - rightStart
        })

        const hasExistingSnapshot = oddsRef.current.length > 0 || receiverDeliveredRows
        const initialSnapshotReady =
          receiverRequests.length === 1 ||
          receiverRowsByLeague.size >= 3 ||
          nextRows.length >= 500

        if (!force && !hasExistingSnapshot && !initialSnapshotReady) {
          return
        }

        oddsRef.current = nextRows
        setOdds(nextRows)
        writeJsonCache(cacheKey, nextRows)
        setStatusNotice(null)
        setError(null)
        setLoading(false)
        receiverDeliveredRows = true
      }

      const receiverResults = await allSettledWithConcurrency(
        receiverRequests,
        selectedLeague.id === 'ALL' ? RECEIVER_ALL_BOARD_CONCURRENCY : 1,
        async ({ sport, limit }) => {
          const normalizedSport = normalizeLeagueCode(sport)
          const result = selectedLeague.id === 'ALL'
            ? await fetchReceiverOdds(
                { sport, limit: String(limit) },
                { timeoutMs: RECEIVER_ODDS_FALLBACK_TIMEOUT_MS }
              )
            : preloadedReceiverResults.has(normalizedSport)
              ? preloadedReceiverResults.get(normalizedSport)
              : await fetchReceiverRequestWithRetry({ sport, limit })
          if (fetchRunRef.current !== fetchRunId) return result

          let resultForSummary = result
          if (result?.status === 'ok' && Array.isArray(result.data?.odds) && result.data.odds.length > 0) {
            const receiverRows = mergeReceiverRows([result])
            const visibleReceiverRows = receiverRows
              .map(normalizeOddsRow)
              .filter((row) => isBoardVisibleRow(row, now, minVisibleStartMs, maxVisibleStartMs))
            const latestVisibleUpdate = visibleReceiverRows
              .map((row) => row.updated_at)
              .filter(Boolean)
              .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null

            resultForSummary = {
              ...result,
              data: {
                ...result.data,
                source: result.data?.source || 'receiver-live-odds',
                generated_at: result.data?.generated_at || latestVisibleUpdate,
                visible_row_count: visibleReceiverRows.length,
              },
            }

            if (visibleReceiverRows.length > 0) {
              receiverRowsByLeague.set(normalizedSport, receiverRows)
              publishReceiverRows(false)
            } else if (result?.message) {
              receiverMessages.push(result.message)
            }
          } else if (result?.message) {
            receiverMessages.push(result.message)
          }

          return resultForSummary
        }
      )

      if (fetchRunRef.current !== fetchRunId) return

      if (selectedLeague.id === 'ALL') {
        setDataProof(summarizeReceiverBoardResults(receiverRequests, receiverResults))
      }

      receiverResults.forEach((settled) => {
        if (settled.status === 'rejected') {
          receiverMessages.push(settled.reason?.message || 'Receiver request failed')
        }
      })

      if (receiverDeliveredRows || receiverRowsByLeague.size > 0) {
        publishReceiverRows(true)
        return
      }

      if (liveBoardRows?.length > 0) {
        oddsRef.current = liveBoardRows
        setOdds(liveBoardRows)
        writeJsonCache(cacheKey, liveBoardRows)
        setStatusNotice(null)
        setError(null)
        setLoading(false)
        return
      }

      if (degradedBoardRows?.length > 0) {
        oddsRef.current = degradedBoardRows
        setOdds(degradedBoardRows)
        writeJsonCache(cacheKey, degradedBoardRows)
        setStatusNotice(null)
        setError(null)
        setLoading(false)
        return
      }

      const receiverMessage = receiverMessages.find(Boolean)

      // FALLBACK: Try Supabase if receiver is unavailable
      if (!supabase) {
        setDataProof((previous) => ({
          ...previous,
          source: 'receiver-unavailable',
          successfulSports: [],
          rowCount: 0,
        }))
        setStatusNotice(receiverMessage ? 'Receiver unavailable and no database fallback is configured.' : null)
        setError(receiverMessage || 'Receiver unavailable and Supabase not configured')
        setLoading(false)
        return
      }

      const minStart = new Date(now - LIVE_ODDS_PAST_WINDOW_HOURS * 3600000).toISOString()
      const maxStart = new Date(now + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600000).toISOString()

      let allRows = []
      let from = 0
      let done = false

      while (!done && from < LIVE_ODDS_MAX_ROWS) {
        let query = supabase
          .from('live_odds')
          .select(BOARD_SELECT_FIELDS)
          .in('market_normalized', ['ML', 'SPREAD', 'TOTAL'])
          .gt('start_time', minStart)
          .lt('start_time', maxStart)
          .order('start_time')
          .range(from, from + LIVE_ODDS_PAGE_SIZE - 1)

        const selectedLeague = leagueLookup.get(league) || leagueLookup.get('ALL')
        if (selectedLeague?.id !== 'ALL' && selectedLeague?.raw?.length > 0) {
          query = query.in('league', selectedLeague.raw)
        }

        const { data, error: err } = await query
        if (err) throw err

        const page = data || []
        allRows = allRows.concat(page)
        done = page.length < LIVE_ODDS_PAGE_SIZE
        from += LIVE_ODDS_PAGE_SIZE
      }

      setOdds(allRows)
      writeJsonCache(cacheKey, allRows)
      setDataProof({
        source: 'supabase-fallback',
        generatedAt: allRows
          .map((row) => row.updated_at)
          .filter(Boolean)
          .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null,
        attemptedSports: [selectedLeague.id],
        successfulSports: allRows.length > 0 ? [selectedLeague.id] : [],
        rowCount: allRows.length,
      })
      setStatusNotice(receiverMessage ? `Receiver hot path unavailable. Showing ${selectedLeague.label} database snapshot fallback.` : null)
      setError(null)
    } catch (e) {
      const msg = e.message || 'Failed to load odds'
      setStatusNotice(null)
      setError(isHtmlError(msg) ? SUPABASE_DOWN_MESSAGE : msg)
    } finally {
      fetchInFlightRef.current = false
      setLoading(false)
    }
  }, [league])

  useEffect(() => {
    // GRID VIEW IS SELF-SUFFICIENT (fix 2026-07-21c). OddsMatrixView fetches the married board itself.
    // Running the legacy pipeline underneath it double-loaded the whole board AND wrote ~11k rows into
    // localStorage via writeJsonCache — blowing the ~5MB quota (the documented storage-quota failure) and
    // making the page crawl. In grid mode we skip the legacy fetch entirely: one loader, no cache writes.
    if (viewMode === 'grid') {
      setLoading(false)
      return undefined
    }

    const cachedOdds = readJsonCache(getLiveOddsCacheKey(league), LIVE_ODDS_CACHE_TTL_MS)
    if (Array.isArray(cachedOdds) && cachedOdds.length > 0) {
      setOdds(cachedOdds)
      setLoading(false)
    }

    fetchOdds()
    const selectedLeague = leagueLookup.get(league) || leagueLookup.get('ALL')
    const clockInterval = setInterval(() => {
      setBoardNowMs(Date.now())
    }, LIVE_ODDS_CLOCK_REFRESH_MS)
    const syncInterval = setInterval(() => {
      const now = Date.now()
      setBoardNowMs(now)

      if (selectedLeague.id === 'ALL') {
        fetchOdds()
        return
      }

      const streamConnected = receiverStreamConnectedRef.current
      const lastStreamMessageMs = receiverStreamLastMessageRef.current
      const streamLooksStale =
        !lastStreamMessageMs || (now - lastStreamMessageMs) > LIVE_ODDS_SYNC_REFRESH_MS * 2

      if (!streamConnected || streamLooksStale) {
        fetchOdds()
      }
    }, LIVE_ODDS_SYNC_REFRESH_MS)

    let channel
    if (supabase && selectedLeague.id === 'ALL') {
      channel = supabase
        .channel('live_odds_updates')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'live_odds',
        }, (payload) => {
          setBoardNowMs(Date.now())
          if (payload.eventType === 'DELETE') {
            setOdds(prev => prev.filter(o => (o.compound_key || o.id) !== payload.old.compound_key))
          } else if (payload.new) {
            setOdds(prev => {
              const idx = prev.findIndex(o => (o.compound_key || o.id) === payload.new.compound_key)
              if (idx >= 0) {
                const updated = [...prev]
                updated[idx] = payload.new
                return updated
              }
              return [...prev, payload.new]
            })
          }
        })
        .subscribe()
    }

    return () => {
      clearInterval(clockInterval)
      clearInterval(syncInterval)
      if (channel) supabase.removeChannel(channel)
    }
  }, [fetchOdds, league, viewMode])

  useEffect(() => {
    if (!ENABLE_STATIC_EXPANSION_FEED) return

    const pendingLeagues = requestedExpansionLeagues.filter((entry) => !loadedExpansionLeagues[entry])
    if (pendingLeagues.length === 0) return
    let cancelled = false
    const loadExpansion = async () => {
      const responses = await Promise.all(
        pendingLeagues.map(async (expansionLeague) => {
          try {
            const cacheKey = `${EXPANSION_CACHE_PREFIX}${expansionLeague}`
            const cachedRows = readJsonCache(cacheKey, EXPANSION_CACHE_TTL_MS)
            if (Array.isArray(cachedRows) && cachedRows.length > 0) {
              return { expansionLeague, rows: cachedRows }
            }
            const response = await fetch(`/data/expansionOutcomes.${expansionLeague}.generated.json`)
            if (!response.ok) throw new Error(`Expansion feed unavailable (${response.status})`)
            const rows = await response.json()
            writeJsonCache(cacheKey, Array.isArray(rows) ? rows : [])
            return { expansionLeague, rows: Array.isArray(rows) ? rows : [] }
          } catch {
            return { expansionLeague, rows: [] }
          }
        })
      )

      if (cancelled) return

      const nextRows = responses.flatMap((entry) => entry.rows)
      if (nextRows.length > 0) {
        setExpansionOdds((prev) => mergeOddsDatasets(prev, nextRows))
      }

      setLoadedExpansionLeagues((prev) => ({
        ...prev,
        ...Object.fromEntries(responses.map(({ expansionLeague }) => [expansionLeague, true])),
      }))
    }

    const idleHandle = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(loadExpansion, { timeout: 1500 })
      : window.setTimeout(loadExpansion, 150)

    return () => {
      cancelled = true
      if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function' && typeof idleHandle === 'number') {
        window.cancelIdleCallback(idleHandle)
      } else {
        clearTimeout(idleHandle)
      }
    }
  }, [requestedExpansionLeagues, loadedExpansionLeagues])

  useEffect(() => {
    const selectedLeague = leagueLookup.get(league) || leagueLookup.get('ALL')
    if (selectedLeague.id === 'ALL') {
      receiverStreamConnectedRef.current = false
      receiverStreamLastMessageRef.current = 0
      return undefined
    }

    let socket = null
    let reconnectTimer = null
    let cancelled = false

    const connect = () => {
      if (cancelled) return

      try {
        socket = new WebSocket(buildReceiverStreamUrl({
          skip_init: 'true',
          sport: selectedLeague.id,
        }))
      } catch {
        receiverStreamConnectedRef.current = false
        reconnectTimer = window.setTimeout(connect, RECEIVER_STREAM_RECONNECT_MS)
        return
      }

      socket.onopen = () => {
        receiverStreamConnectedRef.current = true
        receiverStreamLastMessageRef.current = Date.now()
      }

      socket.onmessage = (event) => {
        if (cancelled) return

        let payload
        try {
          payload = JSON.parse(event.data)
        } catch {
          return
        }

        const now = Date.now()
        receiverStreamConnectedRef.current = true
        receiverStreamLastMessageRef.current = now
        const minVisibleStartMs = now - LIVE_ODDS_PAST_WINDOW_HOURS * 3600000
        const maxVisibleStartMs = now + LIVE_ODDS_FUTURE_WINDOW_HOURS * 3600000
        setBoardNowMs(now)

        if (payload?.type === 'UPDATE' && Array.isArray(payload.data) && payload.data.length > 0) {
          const nextRows = applyReceiverOddsUpdates(
            oddsRef.current,
            payload.data,
            selectedLeague,
            now,
            minVisibleStartMs,
            maxVisibleStartMs
          )

          if (nextRows) {
            oddsRef.current = nextRows
            setOdds(nextRows)
            writeJsonCache(getLiveOddsCacheKey(selectedLeague.id), nextRows)
            setStatusNotice(null)
            setError(null)
          }
          return
        }

        if ((payload?.type === 'SCORES_UPDATE' || payload?.type === 'SCORES_INIT') && Array.isArray(payload.data) && payload.data.length > 0) {
          const nextRows = applyReceiverScoreUpdates(oddsRef.current, payload.data)
          if (nextRows) {
            oddsRef.current = nextRows
            setOdds(nextRows)
            writeJsonCache(getLiveOddsCacheKey(selectedLeague.id), nextRows)
          }
        }
      }

      socket.onclose = () => {
        if (cancelled) return
        receiverStreamConnectedRef.current = false
        reconnectTimer = window.setTimeout(connect, RECEIVER_STREAM_RECONNECT_MS)
      }

      socket.onerror = () => {
        receiverStreamConnectedRef.current = false
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.close()
        }
      }
    }

    connect()

    return () => {
      cancelled = true
      receiverStreamConnectedRef.current = false
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close()
      }
    }
  }, [league])

  // Group odds by game (canonical_id)
  const games = useMemo(() => {
    const gameMap = new Map()
    const pairMap = new Map()
    const teamMap = new Map()

    for (const o of visibleOdds) {
      if (search) {
        const s = search.toLowerCase()
        const match = (o.participants || '').toLowerCase().includes(s) ||
          getNormalizedTeamDisplay(o.away_team, o.league).toLowerCase().includes(s) ||
          getNormalizedTeamDisplay(o.home_team, o.league).toLowerCase().includes(s) ||
          o.away_team.toLowerCase().includes(s) ||
          o.home_team.toLowerCase().includes(s) ||
          o.bookmaker.toLowerCase().includes(s)
        if (!match) continue
      }

      const pairKey = buildNormalizedPairKey(o)
      const rowStart = o.start_time ? new Date(o.start_time).getTime() : null
      let gameKey = null
      let closestDiff = Number.POSITIVE_INFINITY

      for (const candidateKey of pairMap.get(pairKey) || []) {
        const existing = gameMap.get(candidateKey)
        if (!existing) continue
        const existingStart = existing.start_time ? new Date(existing.start_time).getTime() : null
        if (rowStart == null || existingStart == null) {
          gameKey = candidateKey
          break
        }

        const diff = Math.abs(rowStart - existingStart)
        if (diff <= getEventMergeThresholdMs(o.league) && diff < closestDiff) {
          closestDiff = diff
          gameKey = candidateKey
        }
      }

      if (!gameKey) {
        const teamOnlyKey = buildNormalizedTeamOnlyKey(o)
        for (const candidateKey of teamMap.get(teamOnlyKey) || []) {
          const existing = gameMap.get(candidateKey)
          if (!existing) continue
          const existingStart = existing.start_time ? new Date(existing.start_time).getTime() : null
          if (rowStart == null || existingStart == null) continue

          const diff = Math.abs(rowStart - existingStart)
          if (diff <= TEAM_ONLY_MERGE_THRESHOLD_MS && diff < closestDiff) {
            closestDiff = diff
            gameKey = candidateKey
          }
        }
      }

      if (!gameKey) {
        gameKey = o.normalized_event_key || o.canonical_id
      }

      if (!gameMap.has(gameKey)) {
        gameMap.set(gameKey, {
          event_key: gameKey,
          canonical_id: o.canonical_id_normalized || o.canonical_id,
          canonical_ids: new Set(o.canonical_ids || [o.canonical_id].filter(Boolean)),
          normalized_event_key: gameKey,
          away_team: o.away_team,
          home_team: o.home_team,
          participants: o.participants,
          start_time: o.start_time || null,
          league: o.league,
          is_live: false,
          game_status: o.game_status,
          home_score: o.home_score,
          away_score: o.away_score,
          period: o.period,
          clock: o.clock,
          last_updated_at: o.updated_at || null,
          bookmakers: new Set(),
          bookmaker_snapshots: new Map(),
          markets: {},
        })
        pairMap.set(pairKey, [...(pairMap.get(pairKey) || []), gameKey])
        const teamOnlyKey = buildNormalizedTeamOnlyKey(o)
        teamMap.set(teamOnlyKey, [...(teamMap.get(teamOnlyKey) || []), gameKey])
      }

      const game = gameMap.get(gameKey)
      game.bookmakers.add(o.bookmaker)
      ;(o.canonical_ids || [o.canonical_id]).filter(Boolean).forEach((value) => game.canonical_ids.add(value))

      const mKey = `${o.market_normalized}|${o.handicap ?? ''}`
      if (!game.markets[mKey]) {
        game.markets[mKey] = {
          market_normalized: o.market_normalized,
          handicap: o.handicap,
          selections: {},
          selectionMeta: {},
        }
      }
      const market = game.markets[mKey]
      const selectionKey = o.normalized_selection || normalizeSelectionLabel(o) || o.selection
      if (!market.selections[selectionKey]) {
        market.selections[selectionKey] = {}
        market.selectionMeta[selectionKey] = {
          canonical_ids: new Set(),
          selection_variants: new Set(),
        }
      }
      market.selections[selectionKey][o.bookmaker] = o.american_odds
      ;(o.canonical_ids || [o.canonical_id]).filter(Boolean).forEach((value) => market.selectionMeta[selectionKey].canonical_ids.add(value))
      ;(o.selection_variants || [o.selection, o.normalized_selection]).filter(Boolean).forEach((value) => market.selectionMeta[selectionKey].selection_variants.add(value))

      const existingSnapshot = game.bookmaker_snapshots.get(o.bookmaker)
      if (parseTimestampMs(o.updated_at || o.start_time) >= parseTimestampMs(existingSnapshot?.updated_at || existingSnapshot?.start_time)) {
        game.bookmaker_snapshots.set(o.bookmaker, {
          bookmaker: o.bookmaker,
          canonical_id: o.canonical_id,
          away_team: o.away_team,
          home_team: o.home_team,
          start_time: o.start_time,
          updated_at: o.updated_at || o.start_time,
          is_live_effective: o.is_live_effective,
          game_status: o.game_status,
          home_score: o.home_score,
          away_score: o.away_score,
          period: o.period,
          clock: o.clock,
          source_priority: o.source_priority ?? 0,
        })
      }
    }

    const result = Array.from(gameMap.values()).map((g) => {
      const normalizedLeague = normalizeLeagueCode(g.league)
      const bookmakerSnapshots = Array.from(g.bookmaker_snapshots.values())
      const awayTeamVotes = new Map()
      const homeTeamVotes = new Map()
      const orientationVotes = new Map()
      const startTimeVotes = new Map()
      let liveBookCount = 0
      let liveBookWeight = 0
      let primaryLiveBookCount = 0
      let scoreboardEvidence = false
      let hasLiveEvidence = false

      bookmakerSnapshots.forEach((snapshot) => {
        const snapshotWeight = getSnapshotWeight(snapshot, boardNowMs)
        const existingAway = awayTeamVotes.get(snapshot.away_team) || { count: 0, weight: 0 }
        awayTeamVotes.set(snapshot.away_team, {
          count: existingAway.count + 1,
          weight: existingAway.weight + snapshotWeight,
        })
        const existingHome = homeTeamVotes.get(snapshot.home_team) || { count: 0, weight: 0 }
        homeTeamVotes.set(snapshot.home_team, {
          count: existingHome.count + 1,
          weight: existingHome.weight + snapshotWeight,
        })
        const orientationKey = buildOrientationKey(snapshot.away_team, snapshot.home_team, normalizedLeague)
        const existingOrientation = orientationVotes.get(orientationKey) || { count: 0, weight: 0 }
        orientationVotes.set(orientationKey, {
          count: existingOrientation.count + 1,
          weight: existingOrientation.weight + snapshotWeight,
        })
        const trustedStart = getTrustedSnapshotStart(snapshot, boardNowMs)
        if (trustedStart) {
          const startBucket = toStartBucketMs(trustedStart)
          if (startBucket != null) {
            const existingVote = startTimeVotes.get(startBucket) || { count: 0, weight: 0 }
            startTimeVotes.set(startBucket, {
              count: existingVote.count + 1,
              weight: existingVote.weight + snapshotWeight,
            })
          }
        }
        if (snapshot.is_live_effective) {
          liveBookCount += 1
          liveBookWeight += snapshotWeight
          if ((snapshot.source_priority ?? 0) > 0) primaryLiveBookCount += 1
          hasLiveEvidence = true
        }
        if (hasScoreboardEvidence(snapshot)) {
          scoreboardEvidence = true
          hasLiveEvidence = true
        }
      })

      const authoritativeSnapshot = chooseAuthoritativeSnapshot(bookmakerSnapshots, boardNowMs)
      const representativeStart =
        chooseRepresentativeStartTime(startTimeVotes, boardNowMs, { hasLiveEvidence }) ||
        getTrustedSnapshotStart(authoritativeSnapshot, boardNowMs) ||
        getTrustedSnapshotStart({
          start_time: g.start_time,
          updated_at: g.last_updated_at,
          is_live_effective: g.is_live,
          game_status: g.game_status,
          home_score: g.home_score,
          away_score: g.away_score,
          period: g.period,
          clock: g.clock,
        }, boardNowMs)
      const representativeOrientation = chooseRepresentativeOrientation(orientationVotes, normalizedLeague, authoritativeSnapshot || g)
      const representativeAway = representativeOrientation.away || normalizeTeamCode(
        chooseRepresentativeTeam(awayTeamVotes, normalizedLeague) || authoritativeSnapshot?.away_team || g.away_team,
        normalizedLeague
      )
      const representativeHome = representativeOrientation.home || normalizeTeamCode(
        chooseRepresentativeTeam(homeTeamVotes, normalizedLeague) || authoritativeSnapshot?.home_team || g.home_team,
        normalizedLeague
      )
      const distinctBookCount = bookmakerSnapshots.length || g.bookmakers.size
      const representativeStartMs = representativeStart ? new Date(representativeStart).getTime() : Number.NaN
      const nearKickoff = Number.isFinite(representativeStartMs) && representativeStartMs <= boardNowMs + 10 * 60_000
      const liveStatusByBooks =
        liveBookWeight > 0 &&
        (
          scoreboardEvidence ||
          liveBookCount >= 2 ||
          (primaryLiveBookCount >= 1 && nearKickoff) ||
          (distinctBookCount === 1 && nearKickoff)
        )

      return {
        ...g,
        away_team: representativeAway,
        home_team: representativeHome,
        start_time: representativeStart,
        canonical_ids: Array.from(g.canonical_ids),
        league: normalizedLeague,
        game_status: authoritativeSnapshot?.game_status || g.game_status,
        period: authoritativeSnapshot?.period ?? g.period,
        clock: authoritativeSnapshot?.clock ?? g.clock,
        home_score: authoritativeSnapshot?.home_score ?? g.home_score,
        away_score: authoritativeSnapshot?.away_score ?? g.away_score,
        last_updated_at: authoritativeSnapshot?.updated_at || g.last_updated_at,
        is_live: isEffectivelyLive({
          is_live: liveStatusByBooks,
          game_status: authoritativeSnapshot?.game_status || g.game_status,
          updated_at: authoritativeSnapshot?.updated_at || g.last_updated_at,
          start_time: representativeStart,
          league: normalizedLeague,
        }, boardNowMs),
        markets: Object.fromEntries(
          Object.entries(g.markets).map(([key, market]) => [
            key,
            {
              ...market,
              selectionMeta: Object.fromEntries(
                Object.entries(market.selectionMeta || {}).map(([selection, meta]) => [
                  selection,
                  {
                    canonical_ids: Array.from(meta.canonical_ids || []),
                    selection_variants: Array.from(meta.selection_variants || []),
                  },
                ])
              ),
            },
          ])
        ),
        bookmakers: Array.from(g.bookmakers).sort(compareBookmakers),
      }
    })
    return result.sort((a, b) => new Date(a.start_time || 0) - new Date(b.start_time || 0))
  }, [visibleOdds, search, boardNowMs])

  // Group games by league for display
  const gamesByLeague = useMemo(() => {
    if (league !== 'ALL') return [{ league, games, liveCount: games.filter(g => g.is_live).length }]

    const grouped = new Map()
    for (const g of games) {
      const lg = getLeagueBucket(g.league)
      if (!grouped.has(lg)) grouped.set(lg, [])
      grouped.get(lg).push(g)
    }

    // Sort leagues: live games first, then alphabetical
    return Array.from(grouped.entries())
      .map(([lg, gms]) => ({ league: lg, games: gms, liveCount: gms.filter(g => g.is_live).length }))
      .sort((a, b) => {
        if (a.liveCount > 0 && b.liveCount === 0) return -1
        if (b.liveCount > 0 && a.liveCount === 0) return 1
        return a.league.localeCompare(b.league)
      })
  }, [games, league])

  // All unique bookmakers with active rows across the current view.
  const activeBooks = useMemo(() => {
    const books = new Set()
    games.forEach(g => g.bookmakers.forEach(b => books.add(b)))
    return Array.from(books).sort(compareBookmakers)
  }, [games])

  const trackedBooks = useMemo(() => {
    const books = new Set(BOOK_DISPLAY_ORDER)
    games.forEach(g => g.bookmakers.forEach(b => books.add(b)))
    return Array.from(books).sort(compareBookmakers)
  }, [games])

  const listedBooks = useMemo(
    () => (showInactiveTrackedBooks ? trackedBooks : activeBooks),
    [showInactiveTrackedBooks, trackedBooks, activeBooks]
  )

  const enabledBooks = useMemo(
    () => listedBooks.filter((book) => !hiddenBookSet.has(book)),
    [listedBooks, hiddenBookSet]
  )

  // Count unique leagues in data for badge display (game counts)
  const leagueCounts = useMemo(() => {
    const counts = {}
    for (const g of games) {
      const leagueKey = getLeagueBucket(g.league)
      counts[leagueKey] = (counts[leagueKey] || 0) + 1
    }
    return counts
  }, [games])

  // Count odds offerings per league (total individual odds rows)
  const leagueOddsCounts = useMemo(() => {
    const counts = {}
    for (const o of visibleOdds) {
      const lg = getLeagueBucket(normalizeLeagueCode(o.league))
      counts[lg] = (counts[lg] || 0) + 1
    }
    return counts
  }, [visibleOdds])

  // Count offerings per market type (filtered to selected league)
  const marketCounts = useMemo(() => {
    const counts = { ALL: 0, ML: 0, SPREAD: 0, TOTAL: 0 }
    for (const o of visibleOdds) {
      const m = String(o.market_normalized || '').toUpperCase()
      if (counts[m] != null) counts[m]++
      counts.ALL++
    }
    return counts
  }, [visibleOdds])

  // Count offerings per game (for per-event indicator)
  const gameOfferingCounts = useMemo(() => {
    const counts = {}
    for (const g of games) {
      let total = 0
      for (const market of Object.values(g.markets || {})) {
        for (const books of Object.values(market.selections || {})) {
          total += Object.values(books).filter(v => v != null).length
        }
      }
      counts[g.event_key || g.canonical_id] = total
    }
    return counts
  }, [games])

  const totalGames = games.length
  const liveGames = games.filter(g => g.is_live).length
  const hiddenBookCount = listedBooks.length - enabledBooks.length
  const inactiveTrackedCount = Math.max(trackedBooks.length - activeBooks.length, 0)
  const activeBookSet = useMemo(() => new Set(activeBooks), [activeBooks])
  const dataProofAgeMs = dataProof.generatedAt
    ? Math.max(0, boardNowMs - new Date(dataProof.generatedAt).getTime())
    : null
  const dataProofIsFresh = ['receiver-board-live', 'receiver-live-odds'].includes(dataProof.source) && dataProofAgeMs !== null && dataProofAgeMs <= 120_000
  const dataProofSourceLabel = ['receiver-board-live', 'receiver-live-odds'].includes(dataProof.source)
    ? 'Receiver live'
    : dataProof.source === 'supabase-fallback'
      ? 'Database fallback'
      : dataProof.source === 'not-loaded'
        ? 'Waiting for source'
        : 'Source unavailable'

  useEffect(() => {
    if ((visibleLeagueCounts[league] || 0) > 0) return
    if (storedLeaguePreferenceRef.current) return
    const fallbackLeague = getPreferredVisibleLeague(visibleLeagueCounts)
    if (fallbackLeague && fallbackLeague !== league) {
      setLeague(fallbackLeague)
    }
  }, [league, visibleLeagueCounts])

  useEffect(() => {
    setHiddenBooks((previous) => previous.filter((book) => trackedBooks.includes(book)))
  }, [trackedBooks])

  const handleLeagueSelect = useCallback((nextLeague) => {
    storedLeaguePreferenceRef.current = nextLeague
    setLeague(nextLeague)
    setExpandedGame(null)
  }, [])

  const toggleBookVisibility = useCallback((book) => {
    setHiddenBooks((previous) => {
      if (previous.includes(book)) {
        return previous.filter((entry) => entry !== book)
      }
      if (previous.length >= listedBooks.length - 1) {
        return previous
      }
      return [...previous, book]
    })
  }, [listedBooks.length])

  const resetHiddenBooks = useCallback(() => {
    setHiddenBooks([])
  }, [])

  return (
    <div className="widget lot-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <TrendingUp size={16} />
          Live Odds
          <span className="lot-count">{totalGames} games · {activeBooks.length} active / {enabledBooks.length} visible / {trackedBooks.length} tracked books · {visibleOdds.length} odds</span>
          {liveGames > 0 && <span className="lot-live-badge">{liveGames} LIVE</span>}
        </h3>
        <div className="lot-header-actions">
          <button className="lot-icon-btn" onClick={() => setShowSearch(!showSearch)} title="Search">
            <Search size={14} />
          </button>
          <div className="lot-view-toggle">
            <button className={`lot-fmt-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid View (OddsJam style)">⊞</button>
            <button className={`lot-fmt-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Card View">☰</button>
          </div>
          <div className="lot-format-toggle">
            <span
              className="lot-fmt-btn active lot-static-pill"
              title={`Included built books: ${expansionBookNames.join(', ')}`}
            >+{expansionBookNames.length} BOOKS</span>
            <button
              className={`lot-fmt-btn ${oddsFormat === 'american' ? 'active' : ''}`}
              onClick={() => setOddsFormat('american')}
            >US</button>
            <button
              className={`lot-fmt-btn ${oddsFormat === 'decimal' ? 'active' : ''}`}
              onClick={() => setOddsFormat('decimal')}
            >DEC</button>
          </div>
          <button className="widget-refresh-btn" onClick={fetchOdds} title="Refresh">
            <RefreshCw size={14} className={loading ? 'lot-spinner' : ''} />
          </button>
        </div>
      </div>

      <div className={`lot-data-proof ${dataProofIsFresh ? 'is-live' : 'is-degraded'}`}>
        <span className="lot-data-proof-source">{dataProofSourceLabel}</span>
        <span>{dataProof.successfulSports.length}/{dataProof.attemptedSports.length || 1} sports returned data</span>
        <span>{dataProof.rowCount.toLocaleString()} source rows</span>
        <span>
          {dataProof.generatedAt
            ? `Captured ${new Date(dataProof.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`
            : 'No capture timestamp'}
        </span>
        {dataProofAgeMs !== null && <span>{Math.round(dataProofAgeMs / 1000)}s old</span>}
      </div>

      {/* Sport nav pills — with game count + odds count per league */}
      <div className="lot-nav lot-sport-nav">
        {LEAGUES.map(l => {
          const gameCount = l.id === 'ALL' ? totalGames : (leagueCounts[l.id] || 0)
          const oddsCount = l.id === 'ALL' ? visibleOdds.length : (leagueOddsCounts[l.id] || 0)
          const hasLive = l.id === 'ALL' ? liveGames > 0 :
            games.some(g => getLeagueBucket(g.league) === l.id && g.is_live)
          return (
            <button
              key={l.id}
              className={`lot-nav-pill ${league === l.id ? 'active' : ''} ${hasLive ? 'has-live' : ''} ${gameCount === 0 && oddsCount === 0 ? 'is-empty' : ''}`}
              onClick={() => handleLeagueSelect(l.id)}
              title={`${gameCount} games · ${oddsCount.toLocaleString()} odds`}
            >
              {getLeagueLogoPath(l.id) ? (
                <img
                  src={getLeagueLogoPath(l.id)}
                  alt=""
                  className="lot-nav-logo"
                  onError={(event) => { event.target.style.display = 'none' }}
                />
              ) : null}
              <span className="lot-nav-label">{l.label}</span>
              {gameCount > 0 && <span className="lot-nav-count">{gameCount}</span>}
              {oddsCount > 0 && <span className="lot-nav-odds-count">{oddsCount > 999 ? `${(oddsCount / 1000).toFixed(1)}k` : oddsCount}</span>}
              {hasLive && <span className="lot-nav-live-dot" />}
            </button>
          )
        })}
      </div>

      {/* Market nav pills — with count of offerings per market */}
      <div className="lot-nav lot-market-nav">
        {MARKETS.map(m => (
          <button
            key={m.id}
            className={`lot-nav-pill lot-market-pill ${marketFilter === m.id ? 'active' : ''}`}
            onClick={() => setMarketFilter(m.id)}
          >
            {m.label}
            {marketCounts[m.id] > 0 && <span className="lot-nav-count">{marketCounts[m.id] > 999 ? `${(marketCounts[m.id] / 1000).toFixed(1)}k` : marketCounts[m.id]}</span>}
          </button>
        ))}
      </div>

      <div className="lot-books-toolbar">
        <div className="lot-books-toolbar-header">
          <span className="lot-books-toolbar-title">Sportsbooks</span>
          <span className="lot-books-toolbar-meta">
            {enabledBooks.length} visible · {activeBooks.length} active
            {showInactiveTrackedBooks
              ? ` · ${inactiveTrackedCount} inactive tracked`
              : inactiveTrackedCount > 0
                ? ` · ${inactiveTrackedCount} inactive hidden`
                : ''}
            {hiddenBookCount > 0 ? ` · ${hiddenBookCount} hidden` : ''}
          </span>
          {inactiveTrackedCount > 0 && (
            <button
              className={`lot-books-filter ${showInactiveTrackedBooks ? 'active' : ''}`}
              onClick={() => setShowInactiveTrackedBooks((previous) => !previous)}
            >
              {showInactiveTrackedBooks ? 'Hide Inactive' : `Show ${inactiveTrackedCount} Inactive`}
            </button>
          )}
          <button className="lot-books-reset" onClick={resetHiddenBooks} disabled={hiddenBookCount === 0}>Show All</button>
        </div>
        <div className="lot-books-scroll">
          {listedBooks.map((book) => {
            const identity = resolveBookIdentity(book)
            const visible = !hiddenBookSet.has(book)
            const hasLiveRows = activeBookSet.has(book)
            return (
              <button
                key={book}
                className={`lot-book-toggle ${visible ? 'active' : 'inactive'} ${hasLiveRows ? 'has-live' : ''}`}
                onClick={() => toggleBookVisibility(book)}
                title={`${identity.displayName}${visible ? ' - click to hide' : ' - click to show'}`}
                aria-label={`${visible ? 'Hide' : 'Show'} ${identity.displayName}`}
                aria-pressed={visible}
              >
                <SportsbookLogo
                  book={book}
                  className="lot-book-toggle-logo"
                  fallbackClassName="lot-book-toggle-fallback"
                />
                <span className="lot-book-toggle-label">{identity.displayName}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Search bar (toggle) */}
      {showSearch && (
        <div className="lot-search-bar">
          <Search size={14} className="lot-search-icon" />
          <input
            className="lot-search"
            type="text"
            placeholder="Search teams, books..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          {search && (
            <button className="lot-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>
      )}

      {statusNotice && !error && (
        <div className="lot-status-note">{statusNotice}</div>
      )}

      {error && (
        <div className="lot-error">
          {error}
          <button
            onClick={() => fetchOdds()}
            style={{ marginLeft: 8, padding: '2px 10px', fontSize: 12, cursor: 'pointer', background: '#1a3a5c', color: '#7ec8e3', border: '1px solid #234', borderRadius: 4 }}
          >
            Retry
          </button>
        </div>
      )}

      {viewMode === 'grid' ? (
        /* ── GRID VIEW — the PickTheOdds-grade matrix, gate-verified core (17/17), reading the
           married board (CF-KV plane) directly. Self-sufficient: it does NOT depend on this
           widget's legacy pipeline, so it renders even while that pipeline is empty/loading. ── */
        <OddsMatrixView
          league={league}
          marketFilter={marketFilter}
          oddsFormat={oddsFormat}
          onLeagueChange={(next) => setLeague(next)}
        />
      ) : loading && odds.length === 0 ? (
        <div className="lot-loading">Loading live odds...</div>
      ) : games.length === 0 ? (
        <div className="lot-empty">No odds available for current filters</div>
      ) : (
        /* ── CARD VIEW (original accordion style) ── */
        <div className="lot-games">
          {gamesByLeague.map(group => (
            <div key={group.league} className="lot-league-group">
              {/* League header (only when showing All Sports) */}
              {league === 'ALL' && (
                <div className="lot-league-header">
                  {getLeagueLogoPath(group.league) ? (
                    <img
                      src={getLeagueLogoPath(group.league)}
                      alt=""
                      className="lot-league-logo"
                      onError={(event) => { event.target.style.display = 'none' }}
                    />
                  ) : null}
                  <span className="lot-league-name">{LEAGUE_DISPLAY_LABELS[group.league] || group.league}</span>
                  <span className="lot-league-count">{group.games.length} games</span>
                  {group.liveCount > 0 && (
                    <span className="lot-league-live">
                      <span className="lot-live-dot" />
                      {group.liveCount} LIVE
                    </span>
                  )}
                </div>
              )}

              {group.games.map(game => {
                const offeringCount = gameOfferingCounts[game.event_key || game.canonical_id] || 0
                return (
                  <LiveOddsGameCard
                    key={game.event_key || game.normalized_event_key || game.canonical_id}
                    game={game}
                    bookmakers={enabledBooks}
                    isExpanded={expandedGame === (game.event_key || game.normalized_event_key || game.canonical_id)}
                    onToggle={() => {
                      const nextKey = game.event_key || game.normalized_event_key || game.canonical_id
                      setExpandedGame(expandedGame === nextKey ? null : nextKey)
                    }}
                    oddsFormat={oddsFormat}
                    onChartOpen={(modal) => setChartModal(modal)}
                  />
                )
              })}
            </div>
          ))}
        </div>
      )}

      {/* Chart Modal */}
      {chartModal && (
        <OddsChartModal
          isOpen={true}
          onClose={() => setChartModal(null)}
          canonicalId={chartModal.canonicalId}
          canonicalIds={chartModal.canonicalIds}
          selection={chartModal.selection}
          selectionVariants={chartModal.selectionVariants}
          market={chartModal.market}
          handicap={chartModal.handicap}
          currentBooks={chartModal.currentBooks}
          currentTimestamp={chartModal.currentTimestamp}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------
// GameCard — collapsed + expanded view for a single game
// -----------------------------------------------------------------------
function GameCard({ game, isExpanded, onToggle, oddsFormat, onChartOpen }) {
  const markets = Object.values(game.markets)

  // Compute best odds & average per selection per market
  const selectionStats = useMemo(() => {
    const stats = {}
    markets.forEach(m => {
      Object.entries(m.selections).forEach(([sel, books]) => {
        const vals = Object.values(books).filter(v => v != null)
        const maxVal = vals.length > 0 ? Math.max(...vals) : null
        const bestBook = maxVal != null
          ? Object.entries(books).find(([, v]) => v === maxVal)?.[0] || null
          : null
        const avg = averageOdds(vals)
        const key = `${m.market_normalized}|${m.handicap ?? ''}|${sel}`
        stats[key] = { best: maxVal, bestBook, avg }
      })
    })
    return stats
  }, [markets])

  // Compute best ML odds for collapsed preview
  const mlMarket = markets.find(m => m.market_normalized === 'ML')
  const spreadMarket = markets.find(m => m.market_normalized === 'SPREAD')
  const totalMarket = markets.find(m => m.market_normalized === 'TOTAL')

  // Best ML odds for each side
  const bestAwayML = useMemo(() => {
    if (!mlMarket) return null
    const entry = Object.entries(mlMarket.selections).find(([sel]) =>
      sel.toUpperCase().includes(game.away_team.toUpperCase()) || sel === game.away_team
    )
    if (!entry) return Object.entries(mlMarket.selections)[0] || null
    const vals = Object.values(entry[1]).filter(v => v != null)
    if (vals.length === 0) return null
    const best = Math.max(...vals)
    const book = Object.entries(entry[1]).find(([, v]) => v === best)?.[0]
    return { odds: best, book }
  }, [mlMarket, game.away_team])

  const bestHomeML = useMemo(() => {
    if (!mlMarket) return null
    const entry = Object.entries(mlMarket.selections).find(([sel]) =>
      sel.toUpperCase().includes(game.home_team.toUpperCase()) || sel === game.home_team
    )
    if (!entry) {
      const entries = Object.entries(mlMarket.selections)
      return entries.length > 1 ? (() => {
        const vals = Object.values(entries[1][1]).filter(v => v != null)
        if (vals.length === 0) return null
        const best = Math.max(...vals)
        const book = Object.entries(entries[1][1]).find(([, v]) => v === best)?.[0]
        return { odds: best, book }
      })() : null
    }
    const vals = Object.values(entry[1]).filter(v => v != null)
    if (vals.length === 0) return null
    const best = Math.max(...vals)
    const book = Object.entries(entry[1]).find(([, v]) => v === best)?.[0]
    return { odds: best, book }
  }, [mlMarket, game.home_team])

  return (
    <div className={`lot-game ${isExpanded ? 'expanded' : ''} ${game.is_live ? 'lot-game-live' : ''}`}>
      {/* Collapsed header — 7-column grid matching old dashboard */}
      <div className="lot-game-header" onClick={onToggle}>
        {/* Col 1: Teams */}
        <div className="lot-game-teams-col">
          <div className="lot-team-row">
            {getTeamLogo(game.away_team, game.league) && (
              <img src={getTeamLogo(game.away_team, game.league)} alt=""
                className="lot-team-logo" onError={e => { e.target.style.display = 'none' }} />
            )}
            <span className="lot-team-name">
              {getTeamDisplayName(game.away_team, game.league) || game.away_team}
            </span>
          </div>
          <div className="lot-team-row">
            {getTeamLogo(game.home_team, game.league) && (
              <img src={getTeamLogo(game.home_team, game.league)} alt=""
                className="lot-team-logo" onError={e => { e.target.style.display = 'none' }} />
            )}
            <span className="lot-team-name">
              {getTeamDisplayName(game.home_team, game.league) || game.home_team}
            </span>
          </div>
        </div>

        {/* Col 2: Status / Time */}
        <div className="lot-game-status-col">
          {game.is_live ? (
            <div className="lot-live-tag">
              <span className="lot-live-dot" />
              <span>{game.period || 'LIVE'}</span>
            </div>
          ) : (
            <span className="lot-start-time">{formatStartTime(game.start_time)}</span>
          )}
          <span className="lot-time-until">
            {!game.is_live && timeUntil(game.start_time)}
          </span>
        </div>

        {/* Col 3: Score (if live or final) */}
        <div className="lot-game-score-col">
          {(game.away_score != null || game.home_score != null) && (
            <>
              <span className="lot-score">{game.away_score ?? '—'}</span>
              <span className="lot-score">{game.home_score ?? '—'}</span>
            </>
          )}
        </div>

        {/* Col 4: Best ML odds preview */}
        <div className="lot-game-best-col">
          <div className="lot-best-preview">
            <span className={`lot-best-val ${bestAwayML ? 'lot-green' : ''}`}>
              {bestAwayML ? formatOdds(bestAwayML.odds, oddsFormat) : '—'}
            </span>
            {bestAwayML?.book && <SportsbookLogo book={bestAwayML.book} className="lot-mini-logo" showFallback={false} />}
          </div>
          <div className="lot-best-preview">
            <span className={`lot-best-val ${bestHomeML ? 'lot-green' : ''}`}>
              {bestHomeML ? formatOdds(bestHomeML.odds, oddsFormat) : '—'}
            </span>
            {bestHomeML?.book && <SportsbookLogo book={bestHomeML.book} className="lot-mini-logo" showFallback={false} />}
          </div>
        </div>

        {/* Col 5: Books count + expand toggle */}
        <div className="lot-game-expand-col">
          <span className="lot-books-count">{game.bookmakers.length} books</span>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>

      {/* Expanded view: full odds table */}
      {isExpanded && (
        <div className="lot-expanded">
          {markets.map(m => {
            const showLine = m.market_normalized === 'SPREAD' || m.market_normalized === 'TOTAL'
            return (
              <div key={`${m.market_normalized}|${m.handicap}`} className="lot-market">
                <div className="lot-market-label">
                  {m.market_normalized}
                  {m.handicap != null && ` (${m.handicap > 0 ? '+' : ''}${m.handicap})`}
                </div>
                <div className="lot-odds-table">
                  {/* Header row */}
                  <div className="lot-odds-header">
                    <div className="lot-odds-sel-col">Selection</div>
                    {showLine && <div className="lot-odds-line-col">Line</div>}
                    <div className="lot-odds-chart-col">
                      <BarChart3 size={12} />
                    </div>
                    <div className="lot-odds-best-col">Best</div>
                    <div className="lot-odds-avg-col">Avg</div>
                    {game.bookmakers.map(book => {
                      const identity = resolveBookIdentity(book)
                      return (
                        <div key={book} className="lot-odds-book-col" title={identity.displayName}>
                          <SportsbookLogo book={book} className="lot-book-logo" />
                          <span className="lot-book-column-label">{identity.displayName}</span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Data rows */}
                  {Object.entries(m.selections).map(([sel, books]) => {
                    const statsKey = `${m.market_normalized}|${m.handicap ?? ''}|${sel}`
                    const stats = selectionStats[statsKey] || {}
                    const isBestFn = (book) => {
                      const val = books[book]
                      return val != null && val === stats.best
                    }

                    return (
                      <div key={sel} className="lot-odds-row">
                        <div className="lot-odds-sel-col" title={sel}>{sel}</div>

                        {showLine && (
                          <div className="lot-odds-line-col">
                            {formatLine(m.handicap, m.market_normalized, sel)}
                          </div>
                        )}

                        <div className="lot-odds-chart-col">
                          <button
                            className="lot-chart-btn"
                            title="View odds history"
                            onClick={(e) => {
                              e.stopPropagation()
                              onChartOpen({
                                canonicalId: game.canonical_id,
                                selection: sel,
                                market: m.market_normalized,
                                handicap: m.handicap,
                              })
                            }}
                          >
                            <BarChart3 size={12} />
                          </button>
                        </div>

                        {/* Best odds with book logo */}
                        <div className="lot-odds-best-col lot-best-cell">
                          <span className="lot-best-value">{formatOdds(stats.best, oddsFormat)}</span>
                          {stats.bestBook && <SportsbookLogo book={stats.bestBook} className="lot-best-logo" showFallback={false} />}
                        </div>

                        {/* Average odds */}
                        <div className="lot-odds-avg-col lot-avg-cell">
                          {formatOdds(stats.avg, oddsFormat)}
                        </div>

                        {/* Per-book odds */}
                        {game.bookmakers.map(book => {
                          const val = books[book]
                          const best = isBestFn(book)
                          return (
                            <div
                              key={book}
                              className={`lot-odds-cell ${val != null ? '' : 'empty'} ${best ? 'best' : ''}`}
                            >
                              {val != null ? formatOdds(val, oddsFormat) : '—'}
                              {best && val != null && <Star size={8} className="lot-star" />}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
